import { gzip } from 'fflate'
import runtimeJs from '../../../packages/avataros-character-runtime/dist/runtime.bundle.js?raw'
import { buildTar } from './tar.js'
import { compileCharacterBehavior } from './compileCharacter.js'
import { buildCharacterPackage } from './characterPackage.js'
import {
  getLatestBlueprint,
  listAnimationAssets,
  listMaterials,
  listTextureAssets,
  getMaterialMapping,
} from '../assetManagerApi.js'
import { resolveActiveCharacter } from '../characterState.js'
import { withTimeout, fetchWithTimeout, EXPORT_FETCH_TIMEOUT_MS } from './withTimeout.js'

// Progress phases: 'blueprint', 'rig', 'clips', 'materials', 'compressing', 'done'
function report(onProgress, p) { try { onProgress?.(p) } catch { /* never let UI throw break export */ } }

// Async gzip (fflate runs off the main thread) to avoid freezing the UI.
function gzipAsync(data) {
  return new Promise((resolve, reject) => {
    gzip(data, { level: 6 }, (err, out) => (err ? reject(err) : resolve(out)))
  })
}

// Fetch raw bytes for a URL. Throws loudly on failure — no silent fallback
// (CLAUDE.md: server is the source of truth; loud failure beats silent fakery).
async function fetchBytes(url, label) {
  const res = await fetchWithTimeout(url, EXPORT_FETCH_TIMEOUT_MS, label)
  if (!res.ok) throw new Error(`character export: fetch ${res.status} for ${label} (${url})`)
  return new Uint8Array(await res.arrayBuffer())
}

/**
 * Build the character export package bytes.
 *
 * @param {{ id: string, name?: string, rigPath?: string }} character
 * @param {{ version?: string, onProgress?: Function }} opts
 * @returns {{ packageName, fileName, bytes, states, clips }}
 */
export async function buildCharacterExport(character, { version = '1.0.0', onProgress } = {}) {
  const slug = character?.id
  if (!slug) throw new Error('character export: character has no id/slug')

  // ── 1. Blueprint ───────────────────────────────────────────────────────────
  report(onProgress, { phase: 'blueprint', label: 'Fetching blueprint…', loaded: 0, total: 0 })
  const blueprint = await withTimeout(getLatestBlueprint(slug), EXPORT_FETCH_TIMEOUT_MS, 'blueprint fetch')
  const bodyGraph = blueprint?.body
  if (!bodyGraph || !Array.isArray(bodyGraph.nodes)) {
    throw new Error(`character export: no body blueprint found for character "${slug}"`)
  }

  // ── 2. Compile ─────────────────────────────────────────────────────────────
  // Throws loud on unsupported node — let it propagate so the UI shows the error.
  const compiled = compileCharacterBehavior(bodyGraph)
  const statesArray = [...compiled.states]
  const clipsArray  = [...compiled.clips]

  // ── 3. Rig GLB ────────────────────────────────────────────────────────────
  report(onProgress, { phase: 'rig', label: 'Fetching rig GLB…', loaded: 0, total: 0 })
  const rigPath = character?.rigPath
  if (!rigPath) {
    throw new Error(
      `character export: character "${slug}" has no rigPath — upload a rig first`
    )
  }
  const rigBytes = await fetchBytes(rigPath, 'rig GLB')

  // ── 4. Animation clips (only compiled.clips) ────────────────────────────
  report(onProgress, { phase: 'clips', label: 'Fetching clip list…', loaded: 0, total: 0 })
  const allAssets = await withTimeout(listAnimationAssets(slug), EXPORT_FETCH_TIMEOUT_MS, 'animation-assets list')
  const bodyAssets = (allAssets ?? []).filter(a => a.kind === 'body')

  // Build a name→url map from the server rows, then fetch only the clips
  // referenced by the compiled behavior — avoids shipping unused clips.
  const nameToUrl = new Map(bodyAssets.map(a => [a.name, a.file_url]))

  // Validate first (fail loud on a missing clip before any network work).
  for (const clipName of clipsArray) {
    if (!nameToUrl.get(clipName)) {
      throw new Error(
        `character export: compiled behavior references clip "${clipName}" but it is not in the animation-assets list for "${slug}"`
      )
    }
  }
  // Fetch clips in PARALLEL — fetching 13+ GLBs serially over the staging
  // network was the dominant cost and pushed the export past the e2e test's
  // budget. Each fetch is still individually bounded by fetchBytes' timeout.
  const clipsTotal = clipsArray.length
  report(onProgress, { phase: 'clips', label: `Fetching ${clipsTotal} clips…`, loaded: 0, total: clipsTotal })
  const clipFiles = await Promise.all(
    clipsArray.map(async (clipName) => ({
      name: `${clipName}.glb`,
      bytes: await fetchBytes(nameToUrl.get(clipName), `clip ${clipName}`),
    }))
  )

  // ── 5. Materials / textures / mapping (Slice 1 optional) ─────────────────
  // Fetched when available; omitted on empty/null (character falls back to
  // rig's own baked materials, per the index.js TODO comment in characterPackage.js).
  report(onProgress, { phase: 'materials', label: 'Fetching materials…', loaded: 0, total: 0 })
  let materialsJson = null
  let materialMappingJson = null
  let textures = []
  try {
    const [materialRows, textureRows, mapping] = await withTimeout(
      Promise.all([
        listMaterials(slug),
        listTextureAssets(slug),
        getMaterialMapping(slug),
      ]),
      EXPORT_FETCH_TIMEOUT_MS,
      'materials/textures/mapping list',
    )
    if (Array.isArray(materialRows) && materialRows.length > 0) {
      materialsJson = materialRows
    }
    if (Array.isArray(mapping) && mapping.length > 0) {
      materialMappingJson = mapping
    }
    // Textures: fetch bytes for each row that has a file_url.
    for (const tex of (textureRows ?? [])) {
      const url = tex.file_url ?? tex.fileUrl
      if (!url) continue
      const ext = (tex.file_name ?? tex.fileName ?? tex.name ?? '').split('.').pop() || 'png'
      try {
        const bytes = await fetchBytes(url, `texture ${tex.id}`)
        textures.push({ id: tex.id, ext, bytes })
      } catch (e) {
        // Texture fetch failure is non-fatal for Slice 1 — warn but continue.
        console.warn(`[characterExport] texture ${tex.id} fetch failed (non-fatal):`, e.message)
      }
    }
  } catch (e) {
    // Materials/mapping API failure is non-fatal for Slice 1 — warn and continue
    // without them (rig's own embedded materials will be used).
    console.warn('[characterExport] materials/textures fetch failed (non-fatal, using rig materials):', e.message)
    materialsJson = null
    materialMappingJson = null
    textures = []
  }

  // ── 6. Assemble package ───────────────────────────────────────────────────
  const charName = character?.name ?? slug
  const { packageName, files } = buildCharacterPackage({
    id: slug,
    name: charName,
    version,
    behaviorJson: compiled.behaviorJson,
    runtimeJs,
    states: statesArray,
    clips: clipsArray,
    variableDefaults: {},
    rigBytes,
    clipFiles,
    materialsJson,
    textures,
    materialMappingJson,
  })
  const tar = buildTar(files)

  // ── 7. Compress (off the main thread) ────────────────────────────────────
  report(onProgress, { phase: 'compressing', label: 'Compressing… (large files take a moment)', loaded: 0, total: 0 })
  const bytes = await gzipAsync(tar)

  report(onProgress, { phase: 'done', label: 'done', loaded: 1, total: 1 })
  const fileName = `${packageName.replace('@', '').replace('/', '-')}-${version}.tgz`
  return {
    packageName,
    fileName,
    bytes,
    states: statesArray,
    clips: clipsArray,
  }
}

/**
 * Triggers a browser download of the character .tgz package.
 *
 * @param {{ id: string, name?: string, rigPath?: string }} character
 * @param {{ version?: string, onProgress?: Function }} opts
 * @returns {{ fileName, packageName, states, clips }}
 */
export async function downloadCharacterExport(character, opts) {
  const { fileName, bytes, ...rest } = await buildCharacterExport(character, opts)
  const blob = new Blob([bytes], { type: 'application/gzip' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = fileName
  document.body.appendChild(a); a.click(); a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return { fileName, ...rest }
}

// Test/agent hook: resolves the current character automatically so a Playwright
// test can call it with no args. Returns base64-encoded bytes (no download) so
// automated tests can inspect the package content without intercepting file downloads.
if (typeof window !== 'undefined') {
  window.__EXPORT_CHARACTER_PACKAGE__ = async (opts) => {
    // Total-time budget for the automated hook. Over a slow CI network the full
    // export (rig + N clips, all from staging) can run long; without a ceiling a
    // pathological run hangs until Playwright kills the browser (which can drop a
    // core dump that breaks the later Docker Build). Fail loud with "timed out"
    // instead — the e2e test maps that to an env-skip. Real UI exports go through
    // downloadCharacterExport, which is deliberately left unbounded.
    const character = await withTimeout(
      resolveActiveCharacter(), EXPORT_FETCH_TIMEOUT_MS, 'resolve active character')
    if (!character) throw new Error('character export: no active character')
    const { bytes, fileName, packageName, states, clips } =
      await withTimeout(buildCharacterExport(character, opts), 150_000, 'export package build')
    let bin = ''
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i])
    return { base64: btoa(bin), fileName, packageName, states, clips }
  }
}
